/**
 * 파트너 삽입형 SDK — 브라우저 전역 `window.Orbit`로 노출되는 IIFE 번들의 엔트리.
 *
 * 설계 요약:
 * - 스크립트가 `async`/`defer`로 늦게 도착해도, 로드 **전**에 쌓아 둔 명령(`q`)이 유실되지 않게 한다.
 * - 전역에는 퍼사드 객체(`init`, `track`, `q`, `version`)만 두고, 실제 상태는 클로저 안의 `OrbitSDK`에 둔다.
 * - 동일 번들이 실수로 두 번 로드되면, 이미 설치된 API를 재사용해 이중 초기화를 막는다.
 *
 * Meta Pixel 등 서드파티 추적기는 이 코어 번들 범위 밖(옵트인 플러그인)으로 둔다.
 */

/** 빌드/배포 시 package.json과 맞추는 것이 좋다. 파트너 디버깅·지원용 메타데이터. */
const SDK_VERSION = "0.1.3";
const DEFAULT_EVENTS_ENDPOINT = "https://orbit-event.vercel.app";

/**
 * "이 객체가 우리가 설치한 퍼사드인지" 판별용 비열거 심볼.
 * - `in` 연산자로만 검사하고, `JSON.stringify`나 `for...in`에 노출되지 않게 `defineProperty`로 붙인다.
 * - `Symbol.for`는 동일 문자열이면 런타임/iframe 등에서도 동일 심볼을 가리키므로, 번들이 두 번 로드돼도 동일 마커로 인식한다.
 */
const CORE_MARKER = Symbol.for("orbit.sdk.core");

export type OrbitConfig = Record<string, unknown>;

type AnalyticsProvider = "meta_pixel" | "google_analytics";

interface CapturedAnalyticsEvent {
  provider: AnalyticsProvider;
  eventName: string;
  payload?: unknown;
}

/**
 * 커맨드 큐에 넣을 수 있는 명령 타입.
 * - 튜플 첫 번째 요소가 명령 이름, 나머지가 인자(파트너 스텁과 동일한 형태 유지).
 */
type QueuedCommand = readonly ["init", unknown] | readonly ["track", string];

/** 파트너/런타임에 노출되는 공개 API 형태. */
export interface OrbitPublicAPI {
  /**
   * 비동기 로드 대비 커맨드 큐.
   * 파트너는 SDK 스크립트 **이전**에 예를 들어 다음과 같이 쌓을 수 있다:
   *   `window.Orbit = window.Orbit || { q: [] };`
   *   `window.Orbit.q.push(['init', { siteId: '...' }]);`
   * 로드 후에는 `q.push`가 패치되어, 이후에 넣은 항목도 즉시 디스패치된다.
   */
  readonly q: QueuedCommand[];
  init(config?: unknown): void;
  track(eventName: string): void;
  readonly version: string;
}

/**
 * 실제 비즈니스 상태를 들고 있는 내부 코어(전역에 직접 노출하지 않음).
 *
 * init / track 순서 보장:
 * - `init()` 호출 전에 들어온 `track()`은 `pendingTracks`에 쌓였다가, `init()`이 끝난 직후 **쌓인 순서대로** 처리된다.
 * - 이렇게 하면 파트너가 실수로 track → init 순으로 큐에 넣어도 이벤트가 버려지지 않는다.
 */
class OrbitSDK {
  private config: OrbitConfig | null = null;
  private initialized = false;
  /** init 이전 track 호출 보관(선입선출). */
  private readonly pendingTracks: string[] = [];
  private listenersInstalled = false;

  /**
   * SDK 설정 및 "준비 완료" 플래그 설정.
   * - 이미 초기화된 경우 추가 호출은 무시(멱등). 파트너가 init을 중복 호출해도 안전하게 한다.
   * - config가 객체가 아니면 빈 객체로 정규화해 이후 파이프라인에서 일관되게 다룬다.
   */
  init(config?: unknown): void {
    if (this.initialized) {
      return;
    }
    this.config =
      config && typeof config === "object" ? (config as OrbitConfig) : {};
    this.installAnalyticsListeners();
    this.initialized = true;

    // init 직후: init 이전에 쌓인 track들을 순서 유지한 채 처리
    const pending = this.pendingTracks.splice(0, this.pendingTracks.length);
    for (const name of pending) {
      this.track(name);
    }
  }

  /**
   * 이벤트 전송(현재는 console.debug 싱크 — 추후 전송/배치/재시도로 교체).
   * - 초기화 전이면 pendingTracks에만 넣고 반환(init 이후 일괄 처리).
   * - 빈 문자열 등 잘못된 이름은 조용히 무시(파트너 사이트 콘솔 오류 폭주 방지).
   */
  track(eventName: string): void {
    if (!this.initialized) {
      this.pendingTracks.push(eventName);
      return;
    }

    if (typeof eventName !== "string" || eventName.length === 0) {
      return;
    }

    // TODO: 실서비스에서는 여기에 수집 엔드포인트 호출, 샘플링, 배치 큐 등을 연결한다.
    if (typeof console !== "undefined" && console.debug) {
      console.debug("[Orbit] track", { eventName, config: this.config });
    }
  }

  private onCapturedEvent(event: CapturedAnalyticsEvent): void {
    if (typeof event.eventName !== "string" || event.eventName.length === 0) {
      return;
    }

    // 공개 API의 단일 이벤트명 모델과 맞추기 위해 provider를 prefix로 합친다.
    this.track(`${event.provider}:${event.eventName}`);
    this.sendToApi(event);
  }

  private sendToApi(event: CapturedAnalyticsEvent): void {
    const endpoint = this.resolveEventsEndpoint();
    if (!endpoint) {
      return;
    }
    const authParams = this.resolveAuthQueryParams();
    const requestUrl = this.resolveEventRequestUrl(endpoint, authParams.gid);

    const body = JSON.stringify({
      ts: Date.now(),
      provider: event.provider,
      eventName: event.eventName,
      payload: event.payload,
      sig: authParams.sig,
      exp: authParams.exp,
      sdkVersion: SDK_VERSION,
      config: this.config,
    });

    if (typeof fetch !== "function") {
      return;
    }

    void fetch(requestUrl, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body,
      keepalive: true,
      credentials: "include",
    }).catch(() => {
      // 수집 실패로 파트너 페이지 동작을 방해하지 않도록 조용히 무시
    });
  }

  private resolveAuthQueryParams(): {
    gid: string | null;
    sig: string | null;
    exp: string | null;
  } {
    if (typeof location === "undefined") {
      return { gid: null, sig: null, exp: null };
    }
    const params = new URLSearchParams(location.search);
    return {
      gid: this.normalizeQueryValue(params.get("gid")),
      sig: this.normalizeQueryValue(params.get("sig")),
      exp: this.normalizeQueryValue(params.get("exp")),
    };
  }

  private normalizeQueryValue(value: string | null): string | null {
    if (typeof value !== "string") {
      return null;
    }
    const normalized = value.trim();
    return normalized.length > 0 ? normalized : null;
  }

  private resolveEventRequestUrl(endpoint: string, gid: string | null): string {
    if (!gid) {
      return endpoint;
    }

    try {
      const url = new URL(endpoint);
      const normalizedPath = url.pathname.replace(/\/+$/, "");
      url.pathname = `${normalizedPath}/${encodeURIComponent(gid)}`;
      return url.toString();
    } catch {
      return endpoint;
    }
  }

  private resolveEventsEndpoint(): string | null {
    const endpoint =
      this.readStringConfig("eventsApiUrl") ??
      this.readStringConfig("analyticsApiUrl") ??
      this.readStringConfig("apiUrl") ??
      DEFAULT_EVENTS_ENDPOINT;

    try {
      if (endpoint.startsWith("/") && typeof location !== "undefined") {
        return new URL(endpoint, location.origin).toString();
      }
      return new URL(endpoint).toString();
    } catch {
      return null;
    }
  }

  private readStringConfig(key: string): string | null {
    if (!this.config || typeof this.config !== "object") {
      return null;
    }
    const value = this.config[key];
    if (typeof value !== "string") {
      return null;
    }
    const normalized = value.trim();
    return normalized.length > 0 ? normalized : null;
  }

  private installAnalyticsListeners(): void {
    if (this.listenersInstalled) {
      return;
    }
    this.listenersInstalled = true;

    this.installMetaPixelHook();
    this.installGoogleAnalyticsHooks();
  }

  private installMetaPixelHook(): void {
    type FbqFn = (...args: unknown[]) => unknown;
    const globalObj = globalThis as typeof globalThis & {
      fbq?: FbqFn;
    };

    const wrap = (fn: FbqFn): FbqFn => {
      const wrapped: FbqFn = (...args: unknown[]) => {
        this.captureFromFbqArgs(args);
        return fn(...args);
      };
      return wrapped;
    };

    let internalFbq: unknown = globalObj.fbq;
    if (typeof internalFbq === "function") {
      internalFbq = wrap(internalFbq as FbqFn);
    }

    try {
      Object.defineProperty(globalObj, "fbq", {
        configurable: true,
        enumerable: true,
        get() {
          return internalFbq as FbqFn | undefined;
        },
        set: (next: unknown) => {
          if (typeof next === "function") {
            internalFbq = wrap(next as FbqFn);
            return;
          }
          internalFbq = next;
        },
      });
    } catch {
      // 이미 non-configurable인 환경에서는 조용히 포기
    }
  }

  private installGoogleAnalyticsHooks(): void {
    type GtagFn = (...args: unknown[]) => unknown;
    type DataLayerPush = (...items: unknown[]) => number;
    const globalObj = globalThis as typeof globalThis & {
      gtag?: GtagFn;
      dataLayer?: unknown[];
    };

    const wrapGtag = (fn: GtagFn): GtagFn => {
      const wrapped: GtagFn = (...args: unknown[]) => {
        this.captureFromGtagArgs(args);
        return fn(...args);
      };
      return wrapped;
    };

    const patchDataLayerPush = (value: unknown): void => {
      if (!Array.isArray(value)) {
        return;
      }
      const dataLayer = value as unknown[] & {
        push: DataLayerPush;
      };
      const nativePush = dataLayer.push.bind(dataLayer);
      dataLayer.push = (...items: unknown[]) => {
        for (const item of items) {
          this.captureFromDataLayer(item);
        }
        return nativePush(...items);
      };
    };

    let internalGtag: unknown = globalObj.gtag;
    if (typeof internalGtag === "function") {
      internalGtag = wrapGtag(internalGtag as GtagFn);
    }

    let internalDataLayer: unknown = globalObj.dataLayer;
    patchDataLayerPush(internalDataLayer);

    try {
      Object.defineProperty(globalObj, "gtag", {
        configurable: true,
        enumerable: true,
        get() {
          return internalGtag as GtagFn | undefined;
        },
        set: (next: unknown) => {
          if (typeof next === "function") {
            internalGtag = wrapGtag(next as GtagFn);
            return;
          }
          internalGtag = next;
        },
      });

      Object.defineProperty(globalObj, "dataLayer", {
        configurable: true,
        enumerable: true,
        get() {
          return internalDataLayer as unknown[] | undefined;
        },
        set: (next: unknown) => {
          internalDataLayer = next;
          patchDataLayerPush(next);
        },
      });
    } catch {
      // 이미 non-configurable인 환경에서는 조용히 포기
    }
  }

  private captureFromFbqArgs(args: unknown[]): void {
    if (args.length < 2 || typeof args[0] !== "string") {
      return;
    }
    const method = args[0];
    if (method === "track" || method === "trackCustom") {
      const eventName = args[1];
      if (typeof eventName === "string") {
        this.onCapturedEvent({
          provider: "meta_pixel",
          eventName,
          payload: args[2],
        });
      }
      return;
    }
    if (method === "trackSingle" && typeof args[2] === "string") {
      this.onCapturedEvent({
        provider: "meta_pixel",
        eventName: args[2],
        payload: args[3],
      });
    }
  }

  private captureFromGtagArgs(args: unknown[]): void {
    if (args.length < 2 || args[0] !== "event" || typeof args[1] !== "string") {
      return;
    }
    this.onCapturedEvent({
      provider: "google_analytics",
      eventName: args[1],
      payload: args[2],
    });
  }

  private captureFromDataLayer(item: unknown): void {
    if (!item || typeof item !== "object") {
      return;
    }

    const eventName = (item as { event?: unknown }).event;
    if (typeof eventName !== "string" || eventName.length === 0) {
      return;
    }

    // GTM 내부 이벤트는 제외하고, 파트너 정의 이벤트만 수집
    if (eventName.startsWith("gtm.") || eventName.startsWith("gtag.")) {
      return;
    }

    this.onCapturedEvent({
      provider: "google_analytics",
      eventName,
      payload: item,
    });
  }
}

/**
 * 전역에 이미 우리가 붙인 퍼사드가 있는지 확인한다.
 * true면 `install()`은 아무것도 만들지 않고 기존 참조를 그대로 반환한다(중복 `<script>` 방지).
 */
function isOurApi(value: unknown): value is OrbitPublicAPI {
  return (
    typeof value === "object" &&
    value !== null &&
    CORE_MARKER in value &&
    (value as Record<typeof CORE_MARKER, unknown>)[CORE_MARKER] === true
  );
}

/**
 * SDK 로드 **전** 파트너가 둔 스텁에서 `q` 배열을 읽어 정규화한다.
 * - 스텁은 `{ q: [['init', cfg], ['track', 'ev'], ...] }` 형태만 가정한다.
 * - 알 수 없는 항목은 건너뛴다(파트너 오타로 전체 설치가 깨지지 않게).
 */
function readPreviousQueue(existing: unknown): QueuedCommand[] {
  if (!existing || typeof existing !== "object") {
    return [];
  }
  const q = (existing as { q?: unknown }).q;
  if (!Array.isArray(q)) {
    return [];
  }
  const out: QueuedCommand[] = [];
  for (const item of q) {
    if (!Array.isArray(item) || item.length < 2) {
      continue;
    }
    const cmd = item[0];
    if (cmd === "init") {
      out.push(["init", item[1]]);
    } else if (cmd === "track" && typeof item[1] === "string") {
      out.push(["track", item[1]]);
    }
  }
  return out;
}

/**
 * `globalThis.Orbit`에 퍼사드를 한 번 설치한다.
 *
 * 처리 순서:
 * 1) 이미 우리 퍼사드면 → 즉시 반환.
 * 2) 기존 값에서 `previousCommands` 추출(스텁의 `q` 등).
 * 3) 새 `OrbitSDK` 인스턴스 생성.
 * 4) `previousCommands`를 순서대로 `dispatch` → 내부적으로 init/track 반영.
 * 5) 공개 `q` 배열의 `push`를 래핑해, 로드 이후 `q.push([...])`도 즉시 실행되게 한다.
 * 6) 퍼사드에 비열거 `CORE_MARKER`를 붙이고 `globalThis.Orbit`에 할당.
 */
function install(): OrbitPublicAPI {
  const globalObj = globalThis as typeof globalThis & {
    Orbit?: unknown;
  };

  const existing = globalObj.Orbit;
  if (isOurApi(existing)) {
    return existing;
  }

  const sdk = new OrbitSDK();
  const previousCommands = readPreviousQueue(existing);

  const q: QueuedCommand[] = [];

  const dispatch = (command: QueuedCommand): void => {
    if (command[0] === "init") {
      sdk.init(command[1]);
      return;
    }
    if (command[0] === "track") {
      sdk.track(command[1]);
    }
  };

  // 스텁 시절에 쌓여 있던 명령을, 삽입 순서 그대로 반영
  for (const cmd of previousCommands) {
    dispatch(cmd);
  }

  /**
   * `Array.prototype.push`를 보존한 채 `q.push`만 교체한다.
   * - 파트너가 로드 후에도 관습적으로 `Orbit.q.push(['track', 'x'])`를 쓸 수 있다.
   * - 항목을 배열에 넣은 뒤 같은 명령을 `dispatch`해 `init`/`track`과 동작을 맞춘다.
   */
  const nativePush = Array.prototype.push;
  (
    q as QueuedCommand[] & { push: (...items: QueuedCommand[]) => number }
  ).push = function pushPatched(...items: QueuedCommand[]) {
    for (const item of items) {
      nativePush.call(q, item);
      dispatch(item);
    }
    return q.length;
  };

  const api: OrbitPublicAPI = {
    q,
    version: SDK_VERSION,
    init(config?: unknown) {
      sdk.init(config);
    },
    track(eventName: string) {
      sdk.track(eventName);
    },
  };

  Object.defineProperty(api, CORE_MARKER, {
    value: true,
    enumerable: false,
    configurable: false,
    writable: false,
  });

  globalObj.Orbit = api;
  return api;
}

/** 모듈 평가 시점에 한 번 실행되어 전역에 퍼사드를 심는다(Vite lib IIFE의 진입점). */
const api = install();
export default api;
